import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { api, errorMessage } from "../../lib/api";
import { notifyBandejaChanged } from "../../lib/brand";
import { AiJobPanel } from "./AiJobPanel";

const LIVE = ["en_curso", "pausando"];

const Preflight = ({ open, onClose, onStart }) => {
  const [pf, setPf] = useState(null);
  useEffect(() => {
    if (!open) return;
    setPf(null);
    api.get("/ai/preflight").then((r) => setPf(r.data)).catch((err) => { toast.error(errorMessage(err, "No se pudo calcular el costo")); onClose(); });
  }, [open, onClose]);
  const total = pf ? pf.por_clasificar + pf.otros : 0;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ai-preflight-dialog">
        <DialogHeader>
          <DialogTitle>Clasificar con IA</DialogTitle>
          <DialogDescription>Claude Sonnet 5.5 leerá cada archivo y lo acomodará en el cliente y tipo correctos. Los clientes nuevos solo se proponen.</DialogDescription>
        </DialogHeader>
        {!pf ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#0f5ef7]" /></div> : (
          <ul className="space-y-1.5 text-sm" data-testid="ai-preflight-counts">
            <li data-testid="ai-preflight-por-clasificar">Por clasificar: <b>{pf.por_clasificar}</b> archivos</li>
            <li data-testid="ai-preflight-otros">Otros documentos: <b>{pf.otros}</b> archivos</li>
            <li data-testid="ai-preflight-no-legibles">No se pueden leer: <b>{pf.comprimidos}</b> comprimidos (.zip, .rar, .7z){pf.grandes ? ` y ${pf.grandes} de más de 20 MB` : ""}</li>
            <li data-testid="ai-preflight-cost">Costo estimado: <b>US${pf.estimated_usd.toFixed(2)}</b> (prueba de 20: US${pf.trial_usd.toFixed(2)})</li>
            {pf.running && <li className="font-semibold text-[#b54708]">Ya hay una clasificación con IA en curso.</li>}
          </ul>
        )}
        <DialogFooter className="gap-2">
          <button type="button" disabled={!pf || !total} onClick={() => onStart(20)} className="tr-btn-light px-4 py-2 text-sm" data-testid="ai-start-trial-button">Probar con 20 archivos</button>
          <button type="button" disabled={!pf || !total} onClick={() => onStart(null)} className="tr-btn-primary px-4 py-2 text-sm" data-testid="ai-start-all-button">Clasificar todos</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AiRunner = ({ autoOpen, onFinished }) => {
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(!!autoOpen);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const refresh = useCallback(() => api.get("/ai/jobs/current").then((r) => setJob(r.data.job)).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    clearTimeout(timer.current);
    if (job && LIVE.includes(job.status)) {
      timer.current = setTimeout(async () => {
        try {
          const j = (await api.get(`/ai/jobs/${job.id}`)).data;
          setJob(j);
          if (!LIVE.includes(j.status)) {
            if (j.status === "terminado") toast.success(`IA: ${j.counts.movidos || 0} movidos, ${j.counts.tipo_corregido || 0} con tipo corregido`);
            else if (j.message) toast.error(j.message);
            notifyBandejaChanged();
            onFinished?.();
          }
        } catch { setJob({ ...job }); }
      }, 1500);
    }
    return () => clearTimeout(timer.current);
  }, [job, onFinished]);
  const call = async (fn) => {
    setBusy(true);
    try { setJob((await fn()).data); } catch (err) { toast.error(errorMessage(err, "No se pudo completar la acción")); }
    setBusy(false);
  };
  const start = (limit) => { setOpen(false); call(() => api.post("/ai/jobs", { limit })); };
  const onClose = useCallback(() => setOpen(false), []);
  return (
    <div className="space-y-3">
      <button type="button" onClick={() => setOpen(true)} disabled={job && LIVE.includes(job.status)} className="tr-btn-primary px-4 py-2 text-sm" data-testid="ai-open-button">
        <Sparkles className="h-4 w-4" /> Clasificar con IA
      </button>
      {job && !job.dismissed && <AiJobPanel job={job} busy={busy} onPause={() => call(() => api.post(`/ai/jobs/${job.id}/pause`))}
        onResume={() => call(() => api.post(`/ai/jobs/${job.id}/resume`))}
        onDismiss={() => call(() => api.post(`/ai/jobs/${job.id}/dismiss`)).then(() => setJob(null))} />}
      <Preflight open={open} onClose={onClose} onStart={start} />
    </div>
  );
};
